/**
 * Scenarios – ComparePanel
 *
 * Requires 2+ non-promoted scenarios with the same baseline forecast run.
 * Calls useCompareScenarios and shows side-by-side FY and rolling-12 impacts.
 */

import { useState } from "react";
import { AlertCircle, Loader2, X } from "lucide-react";
import { useCompareScenarios } from "@workspace/api-client-react";
import type { ScenarioComparison, ScenarioSummary } from "@workspace/api-client-react";
import { formatVariance } from "@/components/tekledger-ui";
import { StatusBadge } from "./shared";
import { DRIVER_TYPE_LABELS } from "./utils";

export function ComparePanel({
  scenarios,
  onClose,
}: {
  scenarios: ScenarioSummary[];
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ScenarioComparison | null>(null);
  const compareMutation = useCompareScenarios();

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setResult(null);
  };

  const canCompare = selected.size >= 2;

  const handleCompare = () => {
    compareMutation.mutate(
      { data: { scenarioIds: Array.from(selected) } },
      { onSuccess: (data) => setResult(data) }
    );
  };

  // Only non-promoted scenarios can be compared
  const eligible = scenarios.filter((s) => s.status !== "promoted");

  return (
    <div className="sc-compare-panel" data-testid="compare-panel">
      <div className="sc-compare-panel__head">
        <h3>Compare Scenarios</h3>
        <button
          className="sc-icon-btn"
          onClick={onClose}
          data-testid="button-close-compare"
          aria-label="Close compare panel"
        >
          <X size={16} />
        </button>
      </div>

      <p className="sc-compare-panel__hint">
        Select 2 or more compatible scenarios (same fiscal year, department, and
        baseline forecast run) to compare impacts side-by-side.
      </p>

      <div className="sc-compare-select" data-testid="compare-scenario-select">
        {eligible.map((s) => (
          <label
            key={s.scenarioId}
            className={`sc-compare-option ${
              selected.has(s.scenarioId) ? "sc-compare-option--checked" : ""
            }`}
            data-testid={`compare-option-${s.scenarioId}`}
          >
            <input
              type="checkbox"
              checked={selected.has(s.scenarioId)}
              onChange={() => toggle(s.scenarioId)}
            />
            <div>
              <strong>{s.name}</strong>
              <span>
                {DRIVER_TYPE_LABELS[s.questionKey as keyof typeof DRIVER_TYPE_LABELS] ??
                  s.questionKey}
              </span>
            </div>
            <span
              className={
                s.fyImpact > 0
                  ? "bad-text"
                  : s.fyImpact < 0
                  ? "good-text"
                  : ""
              }
            >
              {formatVariance(s.fyImpact)}
            </span>
          </label>
        ))}

        {eligible.length === 0 && (
          <p className="sc-compare-panel__gate">
            No eligible draft scenarios available to compare.
          </p>
        )}
      </div>

      {!canCompare && eligible.length > 0 && (
        <p className="sc-compare-panel__gate" data-testid="compare-gate-message">
          Select at least 2 scenarios to compare.
        </p>
      )}

      <button
        className="tl-btn primary"
        onClick={handleCompare}
        disabled={!canCompare || compareMutation.isPending}
        data-testid="button-run-compare"
      >
        {compareMutation.isPending ? (
          <>
            <Loader2 size={12} className="animate-spin" /> Comparing…
          </>
        ) : (
          <>Compare selected</>
        )}
      </button>

      {compareMutation.isError && (
        <div className="sc-form-error" role="alert" data-testid="compare-error">
          <AlertCircle size={13} />
          <span>
            Comparison failed. Scenarios must share the same baseline forecast
            run.
          </span>
        </div>
      )}

      {result && (
        <div className="sc-compare-result" data-testid="compare-result">
          <div className="sc-label" style={{ marginBottom: 8 }}>
            Baseline: <code>{result.baselineForecastRunId.slice(0, 8)}…</code>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th style={{ textAlign: "right" }}>FY Impact</th>
                  <th style={{ textAlign: "right" }}>Rolling 12M</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {result.scenarios.map((s) => (
                  <tr
                    key={s.scenarioId}
                    data-testid={`compare-row-${s.scenarioId}`}
                  >
                    <td>
                      <strong>{s.name}</strong>
                    </td>
                    <td
                      style={{ textAlign: "right" }}
                      className={
                        s.fyImpact > 0
                          ? "bad-text"
                          : s.fyImpact < 0
                          ? "good-text"
                          : ""
                      }
                    >
                      {formatVariance(s.fyImpact)}
                    </td>
                    <td
                      style={{ textAlign: "right" }}
                      className={
                        s.rolling12Impact > 0
                          ? "bad-text"
                          : s.rolling12Impact < 0
                          ? "good-text"
                          : ""
                      }
                    >
                      {formatVariance(s.rolling12Impact)}
                    </td>
                    <td>
                      <StatusBadge status={s.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
